import React from 'react';
export type ReportColumn = {
  key: string;
  label: string;
  money?: boolean;
  align?: 'right';
};
export type ReportRow = Record<string, string | number> & {
  _onClick?: never;
};
export type ReportSection = {
  title: string;
  note?: string;
  columns: ReportColumn[];
  rows: ReportRow[];
  footer?: ReportRow;
  emptyText?: string;
  /** Screen only: makes rows clickable (drill-down). */
  onRow?: (index: number) => void;
  emphasis?: number[];
};
type Props = {
  section: ReportSection;
  fmt: (v: number) => string;
  variant: 'screen' | 'paper';
};

/** One report table, rendered for the screen or for the A4 print/PDF sheet from the same data. */
export function ReportTable({
  section,
  fmt,
  variant
}: Props) {
  const paper = variant === 'paper';
  const cell = (c: ReportColumn, v: string | number | undefined) => c.money && typeof v === 'number' ? fmt(v) : v ?? '';
  const right = (c: ReportColumn) => c.money || c.align === 'right';
  return <section className={paper ? 'mt-5 break-inside-avoid' : 'rounded-lg border border-slate-200 bg-white'} aria-label={section.title} data-id="element-4642" data-mp="14r07tqix6cxq">
      <header className={paper ? 'mb-1' : 'border-b border-slate-100 px-4 py-2.5'} data-id="element-4643" data-mp="2cta43z926g1s">
        <h3 className={paper ? 'text-[11.5px] font-bold uppercase tracking-wider' : 'text-sm font-semibold text-slate-900'} data-id="element-4644" data-mp="2cveft3d78jcm">{section.title}</h3>
        {section.note && <p className={paper ? 'text-[9px] text-slate-500' : 'text-xs text-slate-500'} data-id="element-4645" data-mp="241rr1x7esgcx">{section.note}</p>}
      </header>
      {section.rows.length === 0 ? <p className={paper ? 'py-2 text-[10px] text-slate-500' : 'px-4 py-6 text-center text-sm text-slate-500'} data-id="element-4646" data-mp="2vrw75jo4yh8c">{section.emptyText ?? 'No records for this period.'}</p> : <div className={paper ? '' : 'overflow-x-auto'} data-id="element-4647" data-mp="2hur3trft3wgs">
          <table className={paper ? 'w-full border-collapse text-[9.5px]' : 'min-w-full text-sm'} data-id="element-4648" data-mp="2mq6fqdu97iak">
            <thead className={paper ? '' : 'bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500'} data-id="element-4649" data-mp="16cgb95z82s0e">
              <tr className={paper ? 'border-b-2 border-slate-800 text-[8.5px] uppercase tracking-wider' : ''} data-id="element-4650" data-mp="1bdm2ezd8i77t">
                {section.columns.map(c => <th key={c.key} className={`${paper ? 'py-1 pr-2' : 'px-3 py-2'} ${right(c) ? 'text-right' : 'text-left'}`} data-id="element-4651" data-mp="n8321xoernv7">{c.label}</th>)}
              </tr>
            </thead>
            <tbody className={paper ? '' : 'divide-y divide-slate-100'} data-id="element-4652" data-mp="ifjrzaoxjav5">
              {section.rows.map((r, i) => {
            const strong = section.emphasis?.includes(i);
            const clickable = !paper && section.onRow;
            return <tr key={i} className={`${paper ? 'border-b border-slate-200' : clickable ? 'cursor-pointer hover:bg-indigo-50/60' : ''} ${strong ? 'font-bold' : ''}`} onClick={clickable ? () => section.onRow!(i) : undefined} onKeyDown={clickable ? e => {
              if (e.key === 'Enter') section.onRow!(i);
            } : undefined} tabIndex={clickable ? 0 : undefined} data-id="element-4653" data-mp="3j2g8ictvu82" data-mpi={i}>
                    {section.columns.map(c => <td key={c.key} className={`${paper ? 'py-1 pr-2' : 'px-3 py-1.5'} ${right(c) ? 'text-right tabular-nums' : ''} ${!paper && clickable && c === section.columns[0] ? 'font-medium text-indigo-700' : ''}`} data-id="element-4654" data-mp="1whr6p3j8l4xh">{cell(c, r[c.key])}</td>)}
                  </tr>;
          })}
            </tbody>
            {section.footer && <tfoot className={paper ? 'border-t-2 border-slate-800 font-bold' : 'border-t-2 border-slate-200 bg-slate-50 font-semibold'} data-id="element-4655" data-mp="2b5ufslskswqi">
                <tr data-id="element-4656" data-mp="1ihoothaxibi8">{section.columns.map(c => <td key={c.key} className={`${paper ? 'py-1 pr-2' : 'px-3 py-2'} ${right(c) ? 'text-right tabular-nums' : ''}`} data-id="element-4657" data-mp="1iirhnurtao0s">{cell(c, section.footer![c.key])}</td>)}</tr>
              </tfoot>}
          </table>
        </div>}
    </section>;
}